import { Camera, Check, Flashlight, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { onBack } from '../lib/backstack';
import { isNativeApp } from '../lib/native';
import { toast } from '../lib/toast';
import { tr } from '../lib/i18n';

interface Detector { detect(src: CanvasImageSource): Promise<Array<{ rawValue: string }>> }
interface DetectorClass { new (o?: { formats: string[] }): Detector; getSupportedFormats?(): Promise<string[]> }
declare global {
  interface Window { BarcodeDetector?: DetectorClass }
}

const FORMATS = ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39', 'itf', 'qr_code', 'data_matrix'];

/** Any camera works: the browser's own detector when it has one, otherwise the bundled ZXing (WebAssembly) decoder. */
export const cameraScanSupported = () => typeof window !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;

/** Which decoder this device uses (shown in Settings › Devices). */
export async function scanEngine(): Promise<'native' | 'zxing'> {
  const formats = await window.BarcodeDetector?.getSupportedFormats?.().catch(() => [] as string[]);
  return formats?.includes('ean_13') ? 'native' : 'zxing';
}

let detector: Promise<Detector> | null = null;
export function getDetector(): Promise<Detector> {
  detector ??= (async () => {
    if ((await scanEngine()) === 'native') {
      const supported = (await window.BarcodeDetector!.getSupportedFormats!()) ?? [];
      return new window.BarcodeDetector!({ formats: FORMATS.filter((f) => supported.includes(f)) });
    }
    const [{ BarcodeDetector, setZXingModuleOverrides }, { default: wasm }] = await Promise.all([
      import('barcode-detector/ponyfill'),
      import('zxing-wasm/reader/zxing_reader.wasm?url'),
    ]);
    setZXingModuleOverrides({ locateFile: (path: string, prefix: string) => (path.endsWith('.wasm') ? wasm : prefix + path) });
    return new BarcodeDetector({ formats: FORMATS as never });
  })();
  detector.catch(() => { detector = null; });
  return detector;
}

/** Android app: Google's own code scanner (Google Play services). Returns false when it isn't available here. */
async function googleScanner(onCode: (code: string) => void, continuous: boolean, stop: { done: boolean }): Promise<boolean> {
  const { BarcodeScanner } = await import('@capacitor-mlkit/barcode-scanning');
  try {
    const { available } = await BarcodeScanner.isGoogleBarcodeScannerModuleAvailable();
    if (!available) {
      toast.info(tr('Getting Google’s barcode scanner ready…'));
      await BarcodeScanner.installGoogleBarcodeScannerModule();
      for (let i = 0; i < 40 && !(await BarcodeScanner.isGoogleBarcodeScannerModuleAvailable()).available; i++) await new Promise((r) => setTimeout(r, 500));
    }
  } catch {
    return false; // no Google Play services: use the in-app camera instead
  }
  // Continuous (billing): Google's scanner reopens after each code until the cashier presses Back.
  while (!stop.done) {
    let code: string | undefined;
    try {
      code = (await BarcodeScanner.scan({ autoZoom: true })).barcodes[0]?.rawValue;
    } catch {
      break; // cancelled
    }
    if (!code) break;
    onCode(code);
    if (!continuous) break;
  }
  return true;
}

/**
 * Barcode scanner. In the Android app it opens Google's code scanner; elsewhere a full-screen camera view
 * in the same style. In continuous mode each code is reported once while it stays in view; the same code
 * counts again only after it has been out of view for a second (scan, move away, scan = 2).
 */
export function Scanner({ open, onClose, onCode, continuous = false }: { open: boolean; onClose(): void; onCode(code: string): void; continuous?: boolean }) {
  const [useWeb, setUseWeb] = useState(!isNativeApp());
  const cb = useRef(onCode);
  cb.current = onCode;
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    if (!open || useWeb) return;
    const stop = { done: false };
    void googleScanner((c) => cb.current(c), continuous, stop).then((ok) => {
      if (!ok) setUseWeb(true);
      else if (!stop.done) close.current();
    });
    return () => { stop.done = true; };
  }, [open, useWeb, continuous]);
  if (!open) return null;
  if (!useWeb) return null; // Google's scanner draws its own screen
  return <WebScanner onClose={onClose} onCode={onCode} continuous={continuous} />;
}

function WebScanner({ onClose, onCode, continuous }: { onClose(): void; onCode(code: string): void; continuous: boolean }) {
  const video = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [last, setLast] = useState<string | null>(null);
  const [count, setCount] = useState(0);
  const [torch, setTorch] = useState<null | boolean>(null); // null: the camera has no torch
  const track = useRef<MediaStreamTrack | null>(null);
  // Callers pass fresh handlers on every render; keep them in refs so the camera isn't restarted (and its
  // memory of codes already read wiped) each time a scan updates the bill.
  const cb = useRef(onCode);
  cb.current = onCode;
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); close.current(); } };
    window.addEventListener('keydown', onKey, true);
    // Phone / browser Back closes the scanner.
    const release = onBack(() => close.current());
    return () => {
      window.removeEventListener('keydown', onKey, true);
      release();
    };
  }, []);

  useEffect(() => {
    if (!cameraScanSupported()) {
      setError('This browser can’t open the camera. Use a USB / Bluetooth scanner or type the code.');
      return;
    }
    let stream: MediaStream | null = null;
    let stop = false;
    const seen = new Map<string, number>();
    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false });
        if (stop) { stream.getTracks().forEach((t) => t.stop()); return; }
        const v = video.current!;
        v.srcObject = stream;
        await v.play();
        track.current = stream.getVideoTracks()[0] ?? null;
        const caps = (track.current?.getCapabilities?.() ?? {}) as { torch?: boolean };
        setTorch(caps.torch ? false : null);
        const detector = await getDetector().catch(() => null);
        if (!detector) { setError('The barcode reader couldn’t start on this device. Use a scanner or type the code.'); return; }
        const tick = async () => {
          if (stop) return;
          try {
            const codes = await detector.detect(v);
            const code = codes[0]?.rawValue;
            const now = Date.now();
            const lastSeen = code ? seen.get(code) : undefined;
            if (code) seen.set(code, now); // refreshed every frame it is visible
            if (code && (lastSeen === undefined || now - lastSeen > 1000)) {
              setLast(code);
              setCount((n) => n + 1);
              if ('vibrate' in navigator) navigator.vibrate?.(30);
              cb.current(code);
              if (!continuous) { close.current(); return; }
            }
          } catch { /* frame not ready */ }
          setTimeout(tick, 120);
        };
        void tick();
      } catch (e) {
        const name = (e as Error).name;
        setError(name === 'NotAllowedError' ? 'Camera permission was denied. Allow the camera for this app, then try again.'
          : name === 'NotFoundError' || name === 'OverconstrainedError' ? 'No camera found on this device.' : 'The camera is busy or unavailable.');
      }
    })();
    return () => { stop = true; stream?.getTracks().forEach((t) => t.stop()); track.current = null; };
  }, [continuous]);

  return (
    <div role="dialog" aria-modal="true" aria-label="Scan barcode" className="anim-fade fixed inset-0 z-[70] overflow-hidden bg-black text-white">
      <video ref={video} playsInline muted className="absolute inset-0 h-full w-full object-cover" />
      {/* Window: everything outside is dimmed, white corners like Google's scanner. */}
      {!error && (
        <div aria-hidden className="pointer-events-none absolute top-1/2 left-1/2 aspect-[3/2] w-[78vw] max-w-[380px] -translate-x-1/2 -translate-y-[60%] rounded-3xl shadow-[0_0_0_100vmax_rgba(0,0,0,.55)]">
          <span className="absolute -top-0.5 -left-0.5 h-10 w-10 rounded-tl-3xl border-t-4 border-l-4 border-white" />
          <span className="absolute -top-0.5 -right-0.5 h-10 w-10 rounded-tr-3xl border-t-4 border-r-4 border-white" />
          <span className="absolute -bottom-0.5 -left-0.5 h-10 w-10 rounded-bl-3xl border-b-4 border-l-4 border-white" />
          <span className="absolute -right-0.5 -bottom-0.5 h-10 w-10 rounded-br-3xl border-r-4 border-b-4 border-white" />
          <span className="scan-line absolute inset-x-6 h-0.5 rounded-full bg-white/80 shadow-[0_0_12px_rgba(255,255,255,.8)]" />
        </div>
      )}
      <div className="absolute inset-x-0 top-0 flex items-center gap-2 bg-gradient-to-b from-black/60 to-transparent px-3 pt-[max(0.75rem,var(--sat))] pb-6">
        <button aria-label="Close" onClick={onClose} className="flex h-12 w-12 items-center justify-center rounded-full bg-white/15 backdrop-blur"><X size={24} /></button>
        <div className="flex-1 text-lg font-bold">{tr('Scan barcode')}</div>
        {torch !== null && (
          <button aria-label={torch ? 'Torch off' : 'Torch on'} aria-pressed={torch}
            onClick={() => { const on = !torch; void track.current?.applyConstraints({ advanced: [{ torch: on } as MediaTrackConstraintSet] }).then(() => setTorch(on)).catch(() => setTorch(null)); }}
            className={`flex h-12 w-12 items-center justify-center rounded-full backdrop-blur ${torch ? 'bg-white text-ink' : 'bg-white/15'}`}><Flashlight size={22} /></button>
        )}
      </div>
      <div className="absolute inset-x-0 bottom-0 flex flex-col items-center gap-3 bg-gradient-to-t from-black/70 to-transparent px-5 pt-10 pb-[max(1.25rem,calc(var(--sab)+0.75rem))] text-center">
        {error ? (
          <div className="flex items-start gap-3 rounded-2xl bg-white px-4 py-3 text-left text-sm font-bold text-ink"><Camera size={20} className="shrink-0" />{error}</div>
        ) : last ? (
          <div className="flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-bold text-ink" role="status"><Check size={16} className="text-primary" />{tr('Added')} <span className="num">{last}</span>{count > 1 ? tr(' · {0} scanned', { 0: count }) : ''}</div>
        ) : (
          <div className="text-base font-semibold">{continuous ? tr('Point at a barcode. Each scan adds one item.') : tr('Point at a barcode')}</div>
        )}
        {continuous && <button onClick={onClose} className="h-12 rounded-full bg-white px-8 font-bold text-ink">{tr('Done')}</button>}
      </div>
    </div>
  );
}
